'use client';

import { useState } from 'react';

interface FacebookDebugProps {
  url: string;
  title: string;
  description: string;
  imageUrl: string;
}

export function FacebookDebug({ url, title, description, imageUrl }: FacebookDebugProps) {
  const [debugResult, setDebugResult] = useState<string>('');

  const testFacebookShare = async () => {
    try {
      // Test Facebook Share Dialog
      if (window.FB) {
        window.FB.ui({
          method: 'share',
          href: url,
          display: 'popup'
        }, (response) => {
          if (response && !response.error_message) {
            setDebugResult('Facebook share successful');
          } else {
            setDebugResult(`Facebook share error: ${response?.error_message || 'Unknown error'}`);
          }
        });
      } else {
        setDebugResult('Facebook SDK not loaded');
      }
    } catch (error) {
      setDebugResult(`Error: ${error}`);
    }
  };

  const testFacebookDebugger = () => {
    const debuggerUrl = `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(url)}`;
    window.open(debuggerUrl, '_blank');
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold mb-4">Facebook Share Debug</h3>
      
      <div className="space-y-2 mb-4">
        <div><strong>URL:</strong> {url}</div>
        <div><strong>Title:</strong> {title}</div>
        <div><strong>Description:</strong> {description}</div>
        <div><strong>Final Image URL:</strong> 
          <div className="bg-yellow-100 p-2 rounded text-sm font-mono break-all">
            {imageUrl}
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {imageUrl.includes('/api/File/download') ? '⚠️ API Endpoint (may not work with Facebook/Twitter)' : '✅ Direct Image URL'}
        </div>
        <div className="text-xs text-gray-500">
          {imageUrl.includes('ecommerce-file.slwtechnology.com/files/') ? '✅ Twitter-friendly URL format' : '⚠️ Check URL format for Twitter'}
        </div>
        <div className="mt-2">
          <strong>Image Preview:</strong>
          <img 
            src={imageUrl} 
            alt="Preview" 
            className="mt-1 max-w-xs h-32 object-cover border rounded"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className="hidden text-red-500 text-sm">❌ Image failed to load</div>
        </div>
      </div>

      <div className="space-x-2 mb-4">
        <button
          onClick={testFacebookShare}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Test Facebook Share
        </button>
        <button
          onClick={testFacebookDebugger}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          Open Facebook Debugger
        </button>
        <button
          onClick={() => {
            const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this listing: ${title}`)}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent('#VehicleListing #ClovisClassics')}`;
            window.open(twitterUrl, '_blank');
          }}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
        >
          Test Twitter Share
        </button>
        <button
          onClick={() => {
            const validatorUrl = `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(url)}`;
            window.open(validatorUrl, '_blank');
          }}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Twitter Card Validator
        </button>
        <button
          onClick={() => {
            const refreshUrl = `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(url)}&refresh=1`;
            window.open(refreshUrl, '_blank');
            setDebugResult('Twitter cache refresh requested!');
          }}
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
        >
          Refresh Twitter Cache
        </button>
        <button
          onClick={() => {
            navigator.clipboard.writeText(imageUrl);
            setDebugResult('Final Image URL copied to clipboard!');
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          Copy Image URL
        </button>
        <button
          onClick={() => {
            // Test image accessibility by opening in new tab
            window.open(imageUrl, '_blank');
            setDebugResult('Image opened in new tab - check if accessible');
          }}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Test Image Access
        </button>
      </div>

      {debugResult && (
        <div className="p-2 bg-yellow-100 border border-yellow-300 rounded">
          <strong>Result:</strong> {debugResult}
        </div>
      )}

      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
        <h4 className="font-semibold text-green-800 mb-2">✅ Twitter Cards Status</h4>
        <div className="text-sm text-green-700 space-y-1">
          <div>• Page fetched successfully</div>
          <div>• 22 metatags were found</div>
          <div>• twitter:card = summary_large_image tag found</div>
          <div>• Card loaded successfully</div>
        </div>
        <div className="mt-2 text-xs text-green-600">
          <strong>Note:</strong> Twitter Cards are working correctly! If preview doesn't show immediately, 
          Twitter may need time to cache the URL. Try refreshing or waiting a few minutes.
        </div>
        <div className="mt-2 text-xs text-orange-600">
          <strong>Image Requirements:</strong> Twitter needs images to be:
          <ul className="ml-4 mt-1 space-y-1">
            <li>• Accessible via HTTPS</li>
            <li>• Under 5MB in size</li>
            <li>• JPG, PNG, WEBP, or GIF format</li>
            <li>• Minimum 300x157 pixels</li>
            <li>• Aspect ratio between 1:1 and 2:1</li>
          </ul>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p><strong>Meta Tags Preview:</strong></p>
        <div className="bg-white p-2 border rounded text-xs overflow-auto">
          <div className="mb-2">
            <strong>Open Graph (Facebook):</strong>
            <pre className="mt-1">
{`<meta property="og:title" content="${title}" />
<meta property="og:description" content="${description}" />
<meta property="og:image" content="${imageUrl}" />
<meta property="og:url" content="${url}" />
<meta property="og:site_name" content="Clovis Classics" />
<meta property="og:type" content="website" />`}
            </pre>
          </div>
          <div>
            <strong>Twitter Cards:</strong>
            <pre className="mt-1">
{`<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="${title}" />
<meta name="twitter:description" content="${description}" />
<meta name="twitter:image" content="${imageUrl}" />
<meta name="twitter:creator" content="@clovisclassics" />
<meta name="twitter:site" content="@clovisclassics" />`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
